import type { GraphEdge, GraphNode } from '../@types'

export interface VisGraphInitOptions {
  container: HTMLElement
}

export interface EditCallbacks {
  onAddNode?: (x: number, y: number) => void
  onAddEdge?: (sourceId: string, targetId: string) => void
  onProposeConnection?: (sourceId: string, targetId: string) => void
  onDeleteNode?: (nodeId: string) => void
  onDeleteEdge?: (edgeId: string) => void
  onDataChange?: (nodes: GraphNode[], edges: GraphEdge[]) => void
  onNodeContextMenu?: (nodeId: string, label: string, color: string, menuX: number, menuY: number, clientX: number, clientY: number) => void
  onEdgeContextMenu?: (edgeId: string, sourceId: string, targetId: string, label: string, menuX: number, menuY: number, clientX: number, clientY: number) => void
}

export interface InitOptions extends EditCallbacks {
  editMode?: boolean
}

export type ToolType = 'select' | 'addNode' | 'addEdge' | 'deleteNode' | 'deleteEdge'
export type GraphTheme = 'light' | 'dark'

type VisNode = {
  id: string
  label: string
  font?: {
    color?: string
    size?: number
    face?: string
    strokeWidth?: number
    strokeColor?: string
  }
  color?: {
    background?: string
    border?: string
    opacity?: number
    highlight?: {
      background?: string
      border?: string
    }
    hover?: {
      background?: string
      border?: string
    }
  }
}

type VisEdge = {
  id: string
  from: string
  to: string
  label?: string
  width?: number
  dashes?: boolean | number[]
  font?: {
    color?: string
    size?: number
    align?: string
    strokeWidth?: number
    strokeColor?: string
  }
  color?:
    | string
    | {
        color?: string
        highlight?: string
        hover?: string
        inherit?: boolean
        opacity?: number
      }
  arrows?:
    | 'to' | 'from' | 'to, from'
    | {
        to?: {
          enabled?: boolean
          scaleFactor?: number
        }
      }
}

type VisNetworkInstance = {
  destroy: () => void
  fit: (options?: any) => void
  moveTo?: (options?: any) => void
  getScale?: () => number
  getViewPosition?: () => { x: number; y: number }
  setSelection: (selection: { nodes?: string[]; edges?: string[] }, options?: any) => void
  focus: (nodeId: string, options?: any) => void
  setData: (data: { nodes: any; edges: any }) => void
  on: (eventName: string, handler: (params: any) => void) => void
  off?: (eventName: string, handler: (params: any) => void) => void
  getConnectedNodes: (nodeId: string) => string[]
  getConnectedEdges: (nodeId: string) => string[]
  getPositions?: (nodeIds?: string[]) => Record<string, { x: number; y: number }>
  redraw?: () => void
  DOMtoCanvas?: (point: { x: number; y: number }) => { x: number; y: number }
  getNodeAt?: (point: { x: number; y: number }) => string | number | undefined
  getEdgeAt?: (point: { x: number; y: number }) => string | number | undefined
}

type VisDataSet<T> = {
  update: (items: Array<{ id: string } & Partial<T>>) => void
  clear: () => void
  add: (items: T[]) => void
  remove?: (ids: string[] | string) => void
}

async function loadVis(): Promise<{
  Network: new (container: HTMLElement, data: { nodes: any; edges: any }, options?: any) => VisNetworkInstance
  DataSet: new <T>(items?: T[]) => VisDataSet<T>
}> {
  // 懒加载，避免未安装 vis-network 时在打包阶段硬失败
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const mod: any = await import('vis-network/standalone')
  return { Network: mod.Network, DataSet: mod.DataSet }
}

function toRgba(color: string | undefined, alpha: number): string {
  if (!color) return `rgba(148,163,184,${alpha})`
  const c = color.trim()
  if (c.startsWith('rgba(')) {
    const body = c.slice(5, -1)
    const parts = body.split(',').map(p => p.trim())
    if (parts.length >= 3) return `rgba(${parts[0]},${parts[1]},${parts[2]},${alpha})`
  }
  if (c.startsWith('rgb(')) {
    const body = c.slice(4, -1)
    return `rgba(${body},${alpha})`
  }
  if (c.startsWith('#')) {
    let hex = c.slice(1)
    if (hex.length === 3) hex = hex.split('').map(x => x + x).join('')
    if (hex.length === 6) {
      const r = parseInt(hex.slice(0, 2), 16)
      const g = parseInt(hex.slice(2, 4), 16)
      const b = parseInt(hex.slice(4, 6), 16)
      return `rgba(${r},${g},${b},${alpha})`
    }
  }
  return c
}

function parseColorToRgb(color?: string): [number, number, number] | null {
  if (!color) return null
  const c = color.trim()
  if (c.startsWith('#')) {
    let hex = c.slice(1)
    if (hex.length === 3) hex = hex.split('').map(x => x + x).join('')
    if (hex.length === 6) {
      const r = parseInt(hex.slice(0, 2), 16)
      const g = parseInt(hex.slice(2, 4), 16)
      const b = parseInt(hex.slice(4, 6), 16)
      return [r, g, b]
    }
  }
  if (c.startsWith('rgb(') || c.startsWith('rgba(')) {
    const body = c.slice(c.indexOf('(') + 1, c.lastIndexOf(')'))
    const parts = body.split(',').map(s => Number.parseFloat(s.trim()))
    if (parts.length >= 3 && parts.every(v => Number.isFinite(v))) {
      return [Math.round(parts[0]), Math.round(parts[1]), Math.round(parts[2])]
    }
  }
  return null
}

function brightenColor(color: string, factor: number): string {
  const rgb = parseColorToRgb(color)
  if (!rgb) return color
  const [r, g, b] = rgb
  const rr = Math.min(255, Math.round(r + (255 - r) * factor))
  const gg = Math.min(255, Math.round(g + (255 - g) * factor))
  const bb = Math.min(255, Math.round(b + (255 - b) * factor))
  return `rgb(${rr},${gg},${bb})`
}

// cubic-bezier 求值：根据 x(t)=progress 反求 y(t)
function cubicBezierAt(progress: number, p1x: number, p1y: number, p2x: number, p2y: number): number {
  const clamp = (v: number) => Math.max(0, Math.min(1, v))
  const x = clamp(progress)

  const sampleCurve = (t: number, a1: number, a2: number) => {
    const inv = 1 - t
    return 3 * inv * inv * t * a1 + 3 * inv * t * t * a2 + t * t * t
  }
  const sampleCurveDerivative = (t: number, a1: number, a2: number) => {
    const inv = 1 - t
    return 3 * inv * inv * a1 + 6 * inv * t * (a2 - a1) + 3 * t * t * (1 - a2)
  }

  let t = x
  for (let i = 0; i < 6; i += 1) {
    const xAtT = sampleCurve(t, p1x, p2x) - x
    const d = sampleCurveDerivative(t, p1x, p2x)
    if (Math.abs(d) < 1e-6) break
    t -= xAtT / d
    t = clamp(t)
  }
  return clamp(sampleCurve(t, p1y, p2y))
}

const LIGHT_THEME = {
  nodeLabel: '#1f2937',
  nodeLabelDim: '#9ca3af',
  nodeFillDim: '#dbe4ef',
  nodeBorderDim: '#c7d2e0',
  edgeLabel: '#6b7280',
  edgeLabelDim: '#c0c7d1',
  edgeHighlight: '#ff0033',
  edgeDefault: '#60a5fa',
  edgeDim: '#cbd7e3'
}

const DARK_THEME = {
  nodeLabel: '#e2e8f0',
  nodeLabelDim: '#64748b',
  nodeFillDim: '#1e293b',
  nodeBorderDim: '#334155',
  edgeLabel: '#cbd5e1',
  edgeLabelDim: '#64748b',
  edgeHighlight: '#f43f5e',
  edgeDefault: '#64748b',
  edgeDim: '#334155'
}

export class VisKnowledgeGraph {
  private container: HTMLElement | null = null
  private network: VisNetworkInstance | null = null
  private nodesDs: VisDataSet<VisNode> | null = null
  private edgesDs: VisDataSet<VisEdge> | null = null
  private nodeMap = new Map<string, VisNode>()
  private edgeMap = new Map<string, VisEdge>()
  private hoverNodeHandler: ((params: any) => void) | null = null
  private blurNodeHandler: ((params: any) => void) | null = null
  private clickHandler: ((params: any) => void) | null = null
  private activeNodeId: string | null = null
  private activeEdgeIds = new Set<string>()
  private activeEdgeColorMap = new Map<string, { edgeColor: string; flowColor: string }>()
  private flowTimer: ReturnType<typeof setInterval> | null = null
  private flowProgress = 0
  private afterDrawingHandler: ((ctx: CanvasRenderingContext2D) => void) | null = null
  private stabilizationDoneHandler: ((params: any) => void) | null = null
  private dragStartHandler: ((params: any) => void) | null = null
  private dragEndHandler: ((params: any) => void) | null = null
  private isEditMode = false
  private toolType: ToolType = 'select'
  private flowPausedByDrag = false
  private theme: GraphTheme = 'light'

  private getThemePalette() {
    return this.theme === 'dark' ? DARK_THEME : LIGHT_THEME
  }

  public setTheme(theme: GraphTheme) {
    this.theme = theme
    this.resetStyles()
    this.network?.redraw?.()
  }

  public hasGraph() {
    return !!this.network
  }

  public async init(options: VisGraphInitOptions, nodes: GraphNode[], edges: GraphEdge[], editOptions?: InitOptions) {
    this.destroy()
    this.container = options.container
    void editOptions
    this.isEditMode = false

    const { Network, DataSet } = await loadVis()

    const visNodes: VisNode[] = nodes.map(n => ({
      id: n.id,
      label: n.label,
      color: {
        background: n.style?.fill,
        border: n.style?.stroke
      }
    }))

    const visEdges: VisEdge[] = edges.map((e, idx) => ({
      id: e.id || `edge-${e.source}-${e.target}-${idx}`,
      from: e.source,
      to: e.target,
      label: e.label,
      color: this.getThemePalette().edgeDefault,
      arrows: { to: { enabled: true, scaleFactor: 0.8 } }
    }))

    this.nodeMap = new Map(visNodes.map(n => [n.id, { ...n }]))
    this.edgeMap = new Map(visEdges.map(e => [e.id, { ...e }]))
    this.nodesDs = new DataSet<VisNode>(visNodes)
    this.edgesDs = new DataSet<VisEdge>(visEdges)

    this.network = new Network(
      this.container,
      { nodes: this.nodesDs as any, edges: this.edgesDs as any },
      {
        autoResize: true,
        interaction: {
          hover: true,
          multiselect: false,
          navigationButtons: false
        },
        nodes: {
          shape: 'dot',
          size: 16,
          font: {
            size: 16,
            face: 'Segoe UI, PingFang SC, system-ui, sans-serif',
            strokeWidth: 0,
            strokeColor: 'rgba(0,0,0,0)'
          },
          borderWidth: 2
        },
        edges: {
          font: {
            size: 12,
            align: 'middle',
            strokeWidth: 0,
            strokeColor: 'rgba(0,0,0,0)'
          },
          smooth: { enabled: true, type: 'dynamic' },
          color: {
            color: this.getThemePalette().edgeDefault,
            highlight: this.getThemePalette().edgeHighlight,
            hover: this.getThemePalette().edgeHighlight,
            inherit: false,
            opacity: 1
          },
          selectionWidth: 0,
          hoverWidth: 0
        },
        physics: {
          enabled: true,
          stabilization: { enabled: true, iterations: 250 },
          solver: 'forceAtlas2Based'
        }
      }
    )

    this.setupAdjacencyHover()
    this.resetStyles()
    this.scheduleFitView()
  }

  public update(nodes: GraphNode[], edges: GraphEdge[]) {
    if (!this.nodesDs || !this.edgesDs) return

    const visNodes: VisNode[] = nodes.map(n => ({
      id: n.id,
      label: n.label,
      color: {
        background: n.style?.fill,
        border: n.style?.stroke
      }
    }))

    const visEdges: VisEdge[] = edges.map((e, idx) => ({
      id: e.id || `edge-${e.source}-${e.target}-${idx}`,
      from: e.source,
      to: e.target,
      label: e.label,
      color: this.getThemePalette().edgeDefault,
      arrows: { to: { enabled: true, scaleFactor: 0.8 } }
    }))

    this.nodeMap = new Map(visNodes.map(n => [n.id, { ...n }]))
    this.edgeMap = new Map(visEdges.map(e => [e.id, { ...e }]))
    this.nodesDs.clear()
    this.edgesDs.clear()
    this.nodesDs.add(visNodes)
    this.edgesDs.add(visEdges)
    this.resetStyles()
    this.scheduleFitView()
  }

  private scheduleFitView() {
    this.fitView()
    window.setTimeout(() => this.fitView(), 120)
    window.setTimeout(() => this.fitView(), 500)
    window.setTimeout(() => this.fitView(), 1200)
  }

  public setEditMode(enabled: boolean) {
    void enabled
    this.isEditMode = false
  }

  public setToolType(type: ToolType) {
    void type
    this.toolType = 'select'
  }

  public getEditData(): { nodes: GraphNode[]; edges: GraphEdge[] } {
    return { nodes: [], edges: [] }
  }

  public updateNodeLabel(nodeId: string, newLabel: string) {
    void nodeId
    void newLabel
  }

  public updateNodeColor(nodeId: string, color: string) {
    void nodeId
    void color
  }

  public updateEdge(edgeId: string, updates: { label?: string; source?: string; target?: string }) {
    void edgeId
    void updates
  }

  public deleteNode(nodeId: string) {
    void nodeId
  }

  public deleteEdge(edgeId: string) {
    void edgeId
  }

  public addNodeAt(x: number, y: number, label: string, type: string = 'entity') {
    void x
    void y
    void label
    void type
  }

  public addEdgeBetween(sourceId: string, targetId: string, label?: string) {
    void sourceId
    void targetId
    void label
  }

  public startConnectFrom(nodeId: string) {
    void nodeId
  }

  public getViewportCenterGraphPoint(): { x: number; y: number } | null {
    return null
  }

  public getNodeDisplayLabel(nodeId: string): string {
    return nodeId
  }

  private setupAdjacencyHover() {
    if (!this.network) return

    this.hoverNodeHandler = (params: any) => {
      const nodeId = params?.node as string | undefined
      if (!nodeId) return
      this.highlightAdjacency(nodeId)
    }

    this.blurNodeHandler = () => {
      // 若已点击锁定节点，则保持聚焦状态
      if (this.activeNodeId) return
      this.resetStyles()
    }

    this.clickHandler = (params: any) => {
      if (params?.nodes?.length) {
        const nodeId = String(params.nodes[0])
        this.activeNodeId = nodeId
        this.highlightAdjacency(nodeId)
        return
      }
      this.activeNodeId = null
      if (!params?.nodes?.length) {
        this.resetStyles()
      }
    }

    this.network.on('hoverNode', this.hoverNodeHandler)
    this.network.on('blurNode', this.blurNodeHandler)
    this.network.on('click', this.clickHandler)
    this.stabilizationDoneHandler = () => {
      this.fitView()
      this.network?.redraw?.()
    }
    this.network.on('stabilizationIterationsDone', this.stabilizationDoneHandler as any)
    this.dragStartHandler = (params: any) => {
      const draggingNode = Array.isArray(params?.nodes) && params.nodes.length > 0
      if (!draggingNode) return
      if (this.flowTimer) {
        this.flowPausedByDrag = true
        this.stopFlowAnimation()
      }
    }
    this.dragEndHandler = () => {
      if (!this.flowPausedByDrag) return
      this.flowPausedByDrag = false
      if (this.activeEdgeIds.size > 0) {
        this.startFlowAnimation()
      }
    }
    this.network.on('dragStart', this.dragStartHandler as any)
    this.network.on('dragEnd', this.dragEndHandler as any)
    this.setupFlowOverlay()
  }

  private setupFlowOverlay() {
    if (!this.network) return
    this.afterDrawingHandler = (ctx: CanvasRenderingContext2D) => {
      if (!this.network) return
      if (this.activeEdgeIds.size === 0) return
      const getPositions = this.network.getPositions
      if (typeof getPositions !== 'function') return

      this.activeEdgeIds.forEach(edgeId => {
        const edge = this.edgeMap.get(edgeId)
        if (!edge) return
        const colorInfo = this.activeEdgeColorMap.get(edgeId)
        const flowColor = colorInfo?.flowColor || this.getThemePalette().edgeHighlight
        const posMap = getPositions.call(this.network, [edge.from, edge.to])
        const p1 = posMap?.[edge.from]
        const p2 = posMap?.[edge.to]
        if (!p1 || !p2) return

        const r = 4.5
        const dx = p2.x - p1.x
        const dy = p2.y - p1.y
        const edgeLen = Math.hypot(dx, dy)
        if (edgeLen < 1e-3) return
        const ux = dx / edgeLen
        const uy = dy / edgeLen
        const nx = -uy
        const ny = ux
        const trailCount = 9
        const trailGap = 0.02
        for (let i = trailCount - 1; i >= 0; i -= 1) {
          let tt = this.flowProgress - i * trailGap
          if (tt < 0) tt += 1
          const x = p1.x + (p2.x - p1.x) * tt
          const y = p1.y + (p2.y - p1.y) * tt
          const t = (trailCount - i) / trailCount
          const strength = t * t
          const coreAlpha = i === 0 ? 0.95 : 0.22 * strength
          const glowAlpha = i === 0 ? 0.78 : 0.14 * strength
          const rr = i === 0 ? r : Math.max(1.5, r - i * 0.45)
          const arrowLen = i === 0 ? rr * 2.8 : rr * 2.2
          const arrowHalfWidth = i === 0 ? rr * 0.95 : rr * 0.75

          ctx.save()
          ctx.globalCompositeOperation = 'source-over'
          const grad = ctx.createRadialGradient(x, y, 0, x, y, rr * 2.1)
          grad.addColorStop(0, `rgba(255,255,255,${glowAlpha.toFixed(3)})`)
          grad.addColorStop(1, 'rgba(255,255,255,0)')
          ctx.fillStyle = grad
          ctx.beginPath()
          ctx.arc(x, y, rr * 2.1, 0, Math.PI * 2)
          ctx.fill()

          // 沿边方向绘制箭头形流动粒子，并加 1px 描边
          const tipX = x + ux * arrowLen * 0.7
          const tipY = y + uy * arrowLen * 0.7
          const baseX = x - ux * arrowLen * 0.3
          const baseY = y - uy * arrowLen * 0.3
          const leftX = baseX + nx * arrowHalfWidth
          const leftY = baseY + ny * arrowHalfWidth
          const rightX = baseX - nx * arrowHalfWidth
          const rightY = baseY - ny * arrowHalfWidth

          ctx.fillStyle = `rgba(255,255,255,${coreAlpha.toFixed(3)})`
          ctx.beginPath()
          ctx.moveTo(tipX, tipY)
          ctx.lineTo(leftX, leftY)
          ctx.lineTo(rightX, rightY)
          ctx.closePath()
          ctx.fill()
          ctx.strokeStyle = i === 0 ? flowColor : `rgba(255,255,255,${Math.max(0.2, coreAlpha).toFixed(3)})`
          ctx.lineWidth = 1
          ctx.stroke()
          ctx.restore()
        }
      })
    }

    this.network.on('afterDrawing', this.afterDrawingHandler as any)
  }

  private highlightAdjacency(centerNodeId: string) {
    if (!this.network || !this.nodesDs || !this.edgesDs) return

    const activeNodeIds = new Set<string>([centerNodeId, ...this.network.getConnectedNodes(centerNodeId)])
    const activeEdgeIds = new Set<string>(this.network.getConnectedEdges(centerNodeId).map(id => String(id)))
    this.activeEdgeIds.clear()
    this.activeEdgeColorMap.clear()

    const nodeUpdates: Array<{ id: string } & Partial<VisNode>> = []
    this.nodeMap.forEach((node, nodeId) => {
      const isActive = activeNodeIds.has(nodeId)
      const baseBg = node.color?.background || '#60a5fa'
      const baseBorder = node.color?.border || '#3b82f6'
      nodeUpdates.push({
        id: node.id,
        color: {
          background: isActive ? baseBg : this.getThemePalette().nodeFillDim,
          border: isActive ? baseBorder : this.getThemePalette().nodeBorderDim,
          opacity: 1,
          highlight: {
            background: isActive ? baseBg : this.getThemePalette().nodeFillDim,
            border: isActive ? baseBorder : this.getThemePalette().nodeBorderDim
          },
          hover: {
            background: isActive ? baseBg : this.getThemePalette().nodeFillDim,
            border: isActive ? baseBorder : this.getThemePalette().nodeBorderDim
          }
        },
        font: {
          ...(node.font || {}),
          color: isActive ? this.getThemePalette().nodeLabel : this.getThemePalette().nodeLabelDim,
          strokeWidth: 0,
          strokeColor: 'rgba(0,0,0,0)'
        }
      })
    })

    const edgeUpdates: Array<{ id: string } & Partial<VisEdge>> = []
    this.edgeMap.forEach((edge, edgeId) => {
      // 优先用 vis 返回的 connectedEdges；若返回值不稳定，则按 from/to 关系兜底判断
      const isActive =
        activeEdgeIds.has(String(edgeId)) ||
        edge.from === centerNodeId ||
        edge.to === centerNodeId
      if (isActive) {
        this.activeEdgeIds.add(edge.id)
      }
      const sourceNodeColor = this.nodeMap.get(edge.from)?.color?.background || this.getThemePalette().edgeDefault
      const edgeHighlightColor = sourceNodeColor
      const flowHighlightColor = brightenColor(sourceNodeColor, 0.4)
      if (isActive) {
        this.activeEdgeColorMap.set(edge.id, {
          edgeColor: edgeHighlightColor,
          flowColor: flowHighlightColor
        })
      }
      edgeUpdates.push({
        id: edge.id,
        width: 1,
        dashes: false,
        arrows: {
          to: {
            enabled: true,
            scaleFactor: 0.8
          }
        },
        color: {
          color: isActive ? edgeHighlightColor : this.getThemePalette().edgeDim,
          highlight: isActive ? edgeHighlightColor : this.getThemePalette().edgeDim,
          hover: isActive ? edgeHighlightColor : this.getThemePalette().edgeDim,
          inherit: false,
          opacity: isActive ? 1 : 0.2
        } as any,
        font: {
          ...(edge.font || {}),
          size: edge.font?.size ?? 12,
          color: isActive ? this.getThemePalette().edgeLabel : this.getThemePalette().edgeLabelDim,
          strokeWidth: 0,
          strokeColor: 'rgba(0,0,0,0)'
        }
      })
    })

    this.nodesDs.update(nodeUpdates)
    this.edgesDs.update(edgeUpdates)
    this.startFlowAnimation()
  }

  private resetStyles() {
    if (!this.nodesDs || !this.edgesDs) return

    const theme = this.getThemePalette()
    this.activeEdgeIds.clear()
    this.activeEdgeColorMap.clear()
    this.stopFlowAnimation()

    const nodeUpdates: Array<{ id: string } & Partial<VisNode>> = []
    this.nodeMap.forEach(node => {
      const baseBg = node.color?.background || '#60a5fa'
      const baseBorder = node.color?.border || '#3b82f6'
      nodeUpdates.push({
        id: node.id,
        color: {
          background: baseBg,
          border: baseBorder,
          opacity: 1,
          highlight: { background: baseBg, border: baseBorder },
          hover: { background: baseBg, border: baseBorder }
        },
        font: {
          ...(node.font || {}),
          color: theme.nodeLabel,
          strokeWidth: 0,
          strokeColor: 'rgba(0,0,0,0)'
        }
      })
    })

    const edgeUpdates: Array<{ id: string } & Partial<VisEdge>> = []
    this.edgeMap.forEach(edge => {
      edgeUpdates.push({
        id: edge.id,
        width: 1,
        dashes: false,
        arrows: { to: { enabled: true, scaleFactor: 0.8 } },
        color: {
          color: theme.edgeDefault,
          highlight: theme.edgeHighlight,
          hover: theme.edgeHighlight,
          inherit: false,
          opacity: 1
        } as any,
        font: {
          ...(edge.font || {}),
          size: edge.font?.size ?? 12,
          color: theme.edgeLabel,
          strokeWidth: 0,
          strokeColor: 'rgba(0,0,0,0)'
        }
      })
    })

    this.nodesDs.update(nodeUpdates)
    this.edgesDs.update(edgeUpdates)
  }

  private startFlowAnimation() {
    if (this.flowPausedByDrag) return
    if (!this.edgesDs || this.activeEdgeIds.size === 0) {
      this.stopFlowAnimation()
      return
    }
    if (this.flowTimer) return

    this.flowTimer = setInterval(() => {
      if (!this.edgesDs || this.activeEdgeIds.size === 0) return
      this.flowProgress += 0.006
      if (this.flowProgress > 1) this.flowProgress = 0
      // 标签轻微呼吸缩放：起点 1x -> 峰值约 1.2x -> 末尾 1x（避免竖排长 label 占满画布）
      this.updateActiveEdgeLabelsByProgress(this.flowProgress)
      this.network?.redraw?.()
    }, 16)
  }

  private updateActiveEdgeLabelsByProgress(progress: number) {
    if (!this.edgesDs || this.activeEdgeIds.size === 0) return

    const labelPulseMax = 1.2
    const labelPulseAmp = labelPulseMax - 1
    let scale = 1
    if (progress <= 0.5) {
      const local = progress / 0.5
      const eased = cubicBezierAt(local, 0.22, 0.61, 0.36, 1)
      scale = 1 + labelPulseAmp * eased
    } else {
      const local = (progress - 0.5) / 0.5
      const eased = cubicBezierAt(local, 0.4, 0, 0.2, 1)
      scale = labelPulseMax - labelPulseAmp * eased
    }

    const updates: Array<{ id: string } & Partial<VisEdge>> = []
    this.activeEdgeIds.forEach(edgeId => {
      const edge = this.edgeMap.get(edgeId)
      const baseSize = edge?.font?.size ?? 12
      updates.push({
        id: edgeId,
        font: {
          ...(edge?.font || {}),
          size: Math.max(10, baseSize * scale),
          color: this.getThemePalette().edgeLabel,
          strokeWidth: 0,
          strokeColor: 'rgba(0,0,0,0)'
        }
      })
    })
    this.edgesDs.update(updates)
    this.network?.redraw?.()
  }

  private stopFlowAnimation() {
    if (this.flowTimer) {
      clearInterval(this.flowTimer)
      this.flowTimer = null
    }
    this.flowProgress = 0
  }

  public fitView() {
    try {
      if (!this.network || !this.container) return
      const w = this.container.clientWidth
      const h = this.container.clientHeight
      if (w <= 0 || h <= 0) return

      this.network.redraw?.()
      this.network.fit({
        animation: false,
        minZoomLevel: 0.2,
        maxZoomLevel: 1.2
      } as any)

      // 某些切换时机会出现异常过小缩放，这里兜底拉回可读范围
      const scale = this.network.getScale?.()
      if (typeof scale === 'number' && scale < 0.2) {
        this.network.moveTo?.({
          scale: 0.45,
          animation: false
        })
      }
    } catch {
      // ignore
    }
  }

  public focusNode(nodeId: string) {
    if (!this.network) return
    try {
      this.network.setSelection({ nodes: [nodeId] }, { unselectAll: true })
      this.network.focus(nodeId, { animation: { duration: 500, easingFunction: 'easeInOutQuad' }, scale: 1.2 })
    } catch {
      // ignore
    }
  }

  public destroy() {
    if (this.network && this.network.off) {
      if (this.hoverNodeHandler) this.network.off('hoverNode', this.hoverNodeHandler)
      if (this.blurNodeHandler) this.network.off('blurNode', this.blurNodeHandler)
      if (this.clickHandler) this.network.off('click', this.clickHandler)
      if (this.afterDrawingHandler) this.network.off('afterDrawing', this.afterDrawingHandler as any)
      if (this.stabilizationDoneHandler) this.network.off('stabilizationIterationsDone', this.stabilizationDoneHandler as any)
      if (this.dragStartHandler) this.network.off('dragStart', this.dragStartHandler as any)
      if (this.dragEndHandler) this.network.off('dragEnd', this.dragEndHandler as any)
    }
    if (this.network) {
      try {
        this.network.destroy()
      } catch {
        // ignore
      }
    }
    this.network = null
    this.nodesDs = null
    this.edgesDs = null
    this.activeNodeId = null
    this.activeEdgeIds.clear()
    this.activeEdgeColorMap.clear()
    this.stopFlowAnimation()
    this.nodeMap.clear()
    this.edgeMap.clear()
    this.hoverNodeHandler = null
    this.blurNodeHandler = null
    this.clickHandler = null
    this.afterDrawingHandler = null
    this.stabilizationDoneHandler = null
    this.dragStartHandler = null
    this.dragEndHandler = null
    this.flowPausedByDrag = false
    this.container = null
  }
}

